import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText, Mail, MessageCircle, Users, ChevronLeft, ChevronRight, ExternalLink, CalendarDays,
} from "lucide-react";
import { CountingCard, CampaignProgressCard } from "./Campaigns/Cards";
import { useFetchBlogsQuery } from "../../stores/api/blogs.api";
import { useFetchContactMessagesQuery } from "../../stores/api/contacts.api";
import { useFetchSubscribersQuery } from "../../stores/api/subscribers.api";
import { useFetchUsersQuery } from "../../stores/api/users.api";
import { ROUTE_PATHS } from "../../constants/routeConstants";
import StatusPill from "../../Components/StatusPill";

/* ── mini pagination hook ─────────────────────────── */
const usePagination = (limit = 5) => {
  const [page, setPage] = useState(1);
  return { page, limit, setPage };
};

/* ── small reusable table ──────────────────────────── */
const MiniTable = ({ title, icon: Icon, color, viewAllLink, loading, headers, rows, page, limit, total, onPage }) => {
  const totalPages = Math.ceil((total || 0) / limit) || 1;
  const navigate = useNavigate();

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm overflow-hidden flex flex-col">
      {/* header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-slate-700">
        <div className="flex items-center gap-2">
          <span className={`p-1.5 rounded-md ${color}`}>
            <Icon size={15} />
          </span>
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{title}</h3>
          {total != null && (
            <span className="ml-1 text-xs bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded-full">
              {total}
            </span>
          )}
        </div>
        {viewAllLink && (
          <button
            onClick={() => navigate(viewAllLink)}
            className="flex items-center gap-1 text-xs text-orange-500 hover:text-orange-600 transition"
          >
            View all <ExternalLink size={11} />
          </button>
        )}
      </div>

      {/* table */}
      <div className="overflow-x-auto flex-1">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 dark:bg-slate-700/50">
              {headers.map((h) => (
                <th key={h.key} className="px-4 py-2.5 text-left font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 dark:divide-slate-700">
            {loading ? (
              Array.from({ length: limit }).map((_, i) => (
                <tr key={i}>
                  {headers.map((h) => (
                    <td key={h.key} className="px-4 py-2.5">
                      <div className="h-3 rounded bg-gray-100 dark:bg-slate-700 animate-pulse" style={{ width: `${60 + Math.random() * 30}%` }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={headers.length} className="px-4 py-6 text-center text-gray-400">No records found.</td>
              </tr>
            ) : (
              rows.map((row, i) => (
                <tr key={i} className="hover:bg-gray-50 dark:hover:bg-slate-700/40 transition">
                  {headers.map((h) => (
                    <td key={h.key} className="px-4 py-2.5 text-gray-700 dark:text-gray-300 max-w-[180px] truncate">
                      {h.render ? h.render(row, i) : (row[h.key] ?? "—")}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* pagination */}
      <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 dark:border-slate-700 text-xs text-gray-500">
        <span>Page {page} of {totalPages}</span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onPage(page - 1)}
            disabled={page <= 1}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30 transition"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            onClick={() => onPage(page + 1)}
            disabled={page >= totalPages}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30 transition"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Dashboard ─────────────────────────────────────── */
const Dashboard = () => {
  const blogPg    = usePagination(5);
  const contactPg = usePagination(5);
  const subPg     = usePagination(5);

  const { data: blogsData,    isFetching: blogLoading }    = useFetchBlogsQuery({ page: blogPg.page,    limit: blogPg.limit });
  const { data: contactsData, isFetching: contactLoading } = useFetchContactMessagesQuery({ page: contactPg.page, limit: contactPg.limit });
  const { data: subsData,     isFetching: subLoading }     = useFetchSubscribersQuery({ page: subPg.page,     limit: subPg.limit });
  const { data: usersData,    isFetching: usersLoading }   = useFetchUsersQuery({ page: 1, limit: 1 });

  const blogs    = blogsData?.data    ?? [];
  const contacts = contactsData?.data ?? [];
  const subs     = subsData?.data     ?? [];

  const blogTotal    = blogsData?.total_items    ?? 0;
  const contactTotal = contactsData?.total_items ?? 0;
  const subTotal     = subsData?.total_items     ?? 0;

  const statusBlogMap = { 0: "Draft", 1: "Published", 2: "Archived" };

  const upcomingEvents = [
    { name: "Summer Sale 2025",      status: "Upcoming", startDate: "2025-06-01T10:00:00", endDate: "2025-06-30T23:59:00" },
    { name: "Eid Collection Launch", status: "Planned",  startDate: "2025-03-28T09:00:00", endDate: "2025-04-05T22:00:00" },
    { name: "Back to School",        status: "Active",   startDate: "2025-08-10T08:00:00", endDate: "2025-08-25T20:00:00" },
    { name: "Winter Clearance",      status: "Planned",  startDate: "2025-12-01T10:00:00", endDate: "2025-12-31T23:59:00" },
  ];

  return (
    <div className="m-3 space-y-6">

      {/* ── Stat cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        <CountingCard title="Total Blogs"       Color="blue"   count={blogLoading    ? "…" : blogTotal}    icon={FileText}      />
        <CountingCard title="Contact Messages"  Color="orange" count={contactLoading ? "…" : contactTotal} icon={MessageCircle} />
        <CountingCard title="Subscribers"       Color="green"  count={subLoading     ? "…" : subTotal}     icon={Mail}          />
        <CountingCard title="Total Users"       Color="purple" count={usersLoading ? "…" : (usersData?.total_items ?? 0)} icon={Users} />
      </div>

      {/* ── Latest Blogs (full width) ── */}
      <MiniTable
        title="Latest Blogs"
        icon={FileText}
        color="bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-300"
        viewAllLink={ROUTE_PATHS.ADMIN_BLOG}
        loading={blogLoading}
        page={blogPg.page}
        limit={blogPg.limit}
        total={blogTotal}
        onPage={blogPg.setPage}
        headers={[
          { key: "id",     label: "#",      render: (_, i) => (blogPg.page - 1) * blogPg.limit + i + 1 },
          { key: "title",  label: "Title" },
          { key: "author", label: "Author", render: (r) => r.author || "—" },
          { key: "status", label: "Status", render: (r) => (
            <StatusPill
              value={statusBlogMap[r.status] ?? "Draft"}
              variant={r.status === 1 ? "success" : r.status === 2 ? "neutral" : "warning"}
            />
          )},
          { key: "createdAt", label: "Date", render: (r) => r.createdAt ? new Date(r.createdAt).toLocaleDateString() : "—" },
        ]}
        rows={blogs}
      />

      {/* ── Contact + Subscribers side by side ── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">

        <MiniTable
          title="Contact Messages"
          icon={MessageCircle}
          color="bg-orange-100 text-orange-600 dark:bg-orange-500/20 dark:text-orange-300"
          viewAllLink={ROUTE_PATHS.CONTACT_MESSAGES}
          loading={contactLoading}
          page={contactPg.page}
          limit={contactPg.limit}
          total={contactTotal}
          onPage={contactPg.setPage}
          headers={[
            { key: "id",      label: "#",       render: (_, i) => (contactPg.page - 1) * contactPg.limit + i + 1 },
            { key: "name",    label: "Name" },
            { key: "email",   label: "Email" },
            { key: "subject", label: "Subject", render: (r) => r.subject || "—" },
          ]}
          rows={contacts}
        />

        <MiniTable
          title="Newsletter Subscribers"
          icon={Mail}
          color="bg-green-100 text-green-600 dark:bg-green-500/20 dark:text-green-300"
          viewAllLink={ROUTE_PATHS.SUBSCRIBERS_LIST}
          loading={subLoading}
          page={subPg.page}
          limit={subPg.limit}
          total={subTotal}
          onPage={subPg.setPage}
          headers={[
            { key: "id",    label: "#",     render: (_, i) => (subPg.page - 1) * subPg.limit + i + 1 },
            { key: "email", label: "Email" },
            { key: "createdAt", label: "Joined", render: (r) => r.createdAt ? new Date(r.createdAt).toLocaleDateString() : "—" },
          ]}
          rows={subs}
        />

      </div>

      {/* ── Upcoming Events ── */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <span className="p-1.5 rounded-md bg-purple-100 text-purple-600 dark:bg-purple-500/20 dark:text-purple-300">
              <CalendarDays size={15} />
            </span>
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Upcoming Events</h3>
            <span className="ml-1 text-xs bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded-full">
              {upcomingEvents.length}
            </span>
          </div>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 p-4">
          {upcomingEvents.map((event, i) => (
            <CampaignProgressCard key={i} campaign={event} />
          ))}
        </div>
      </div>

    </div>
  );
};

export default Dashboard;
